import { Button, DatePicker, Form, Select, Typography } from "antd";
import { LeaseApplicationPageLoaderType } from "./loaders";
import { useFetcher, useLoaderData, useNavigation } from "react-router-dom";
import { Content } from "antd/es/layout/layout";
import * as APITypes from "~/API";
import {
  FormattedBuildingData,
  FormattedUnitData,
  LeaseApplicationFormValues,
  LeaseApplicationSubmission,
} from "~/types";
import { INTENTS } from "~/utils/constants";
import dayjs from "dayjs";
import { useAppTranslation } from "~/hooks/useAppTranslation";

export const LeaseApplicationPage: React.FC = () => {
  const fetcher = useFetcher();
  const loaderData = useLoaderData() as LeaseApplicationPageLoaderType;
  const { t } = useAppTranslation();
  const isLoading = fetcher.state !== "idle";
  const navigation = useNavigation();
  const isPageLoading = navigation.state !== "idle";

  const handleSubmit = async (values: LeaseApplicationFormValues) => {
    let endDate = "";

    switch (values.term) {
      case APITypes.LeaseTypeOptions.ThreeMonths:
        endDate = dayjs(values.moveinDate).add(3, "month").toISOString();
        break;
      case APITypes.LeaseTypeOptions.SixMonths:
        endDate = dayjs(values.moveinDate).add(6, "month").toISOString();
        break;
      case APITypes.LeaseTypeOptions.OneYear:
        endDate = dayjs(values.moveinDate).add(1, "year").toISOString();
        break;
      case APITypes.LeaseTypeOptions.TwoYears:
        endDate = dayjs(values.moveinDate).add(2, "year").toISOString();
        break;
      case APITypes.LeaseTypeOptions.ThreeYears:
        endDate = dayjs(values.moveinDate).add(3, "year").toISOString();
        break;
      default: // Includes case for type Monthly
        endDate = dayjs(values.moveinDate).add(1, "month").toISOString();
        break;
    }

    const applicationData: LeaseApplicationSubmission = {
      entityType: loaderData.entityType,
      entityID:
        loaderData.entityType === APITypes.EntityType.Unit
          ? (loaderData[APITypes.EntityType.Unit] as FormattedUnitData)
              .unitDetails.id
          : (loaderData[APITypes.EntityType.Building] as FormattedBuildingData)
              .buildingDetails.id,
      startDate: dayjs().toISOString(),
      endDate: endDate,
      type: values.term,
      moveInDatePreference: dayjs(values.moveinDate).toISOString(),
      rentPaymentPreference: values.paymentPreference,
      userID:
        loaderData.entityType === APITypes.EntityType.Unit
          ? (loaderData[APITypes.EntityType.Unit] as FormattedUnitData)
              .unitDetails.userID || ""
          : (loaderData[APITypes.EntityType.Building] as FormattedBuildingData)
              .buildingDetails.userID || "",
      rent:
        loaderData.entityType === APITypes.EntityType.Unit
          ? (loaderData[APITypes.EntityType.Unit] as FormattedUnitData)
              .unitDetails.rent || 0
          : Number(
              (
                loaderData[
                  APITypes.EntityType.Building
                ] as FormattedBuildingData
              ).buildingDetails.rent
            ) || 0,
    };

    fetcher.submit(
      {
        intent: INTENTS._submit_lease_application,
        body: JSON.stringify(applicationData),
      },
      { method: "post" }
    );

    form.resetFields();
  };

  const [form] = Form.useForm();

  return (
    <Content>
      <div className="centered-form">
        <Typography.Title level={1} className="centered-form-title">
          {t("Lease_Application")}
          <Typography.Paragraph type="secondary">
            {`${loaderData.entityType}: ${
              loaderData.entityType === APITypes.EntityType.Unit
                ? (loaderData[APITypes.EntityType.Unit] as FormattedUnitData)
                    .unitDetails.name
                : (
                    loaderData[
                      APITypes.EntityType.Building
                    ] as FormattedBuildingData
                  ).buildingDetails.name
            }`}
          </Typography.Paragraph>
        </Typography.Title>
        <Form
          form={form}
          onFinish={handleSubmit}
          disabled={isPageLoading || isLoading}
        >
          <Form.Item>
            <Form.Item
              label={t("Move_in_Date_Preference")}
              name="moveinDate"
              rules={[
                {
                  required: true,
                  message: t("Please_enter_move_in_date_preference"),
                },
              ]}
            >
              <DatePicker disabled={isPageLoading} />
            </Form.Item>
            <Form.Item
              label={t("Lease_Term")}
              name="term"
              rules={[
                {
                  required: true,
                  message: t("Please_select_lease_term"),
                },
              ]}
            >
              <Select disabled={isPageLoading}>
                <Select.Option value={APITypes.LeaseTypeOptions.Monthly}>
                  {t("Monthly")}
                </Select.Option>
                <Select.Option value={APITypes.LeaseTypeOptions.ThreeMonths}>
                  {t("3_months")}
                </Select.Option>
                <Select.Option value={APITypes.LeaseTypeOptions.SixMonths}>
                  {t("6_months")}
                </Select.Option>
                <Select.Option value={APITypes.LeaseTypeOptions.OneYear}>
                  {t("1_year")}
                </Select.Option>
                <Select.Option value={APITypes.LeaseTypeOptions.TwoYears}>
                  {t("2_years")}
                </Select.Option>
                <Select.Option value={APITypes.LeaseTypeOptions.ThreeYears}>
                  {t("3_years")}
                </Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              label={t("Preferred_Rent_Payment_Method")}
              name="paymentPreference"
              rules={[
                {
                  required: true,
                  message: t("Please_enter_rent_payment_preference"),
                },
              ]}
            >
              <Select disabled={isPageLoading}>
                <Select.Option value="BankTransfer">
                  {t("Bank_Transfer")}
                </Select.Option>
                <Select.Option value="Card">
                  {t("Credit_Debit_Card")}
                </Select.Option>
                <Select.Option value="Check">{t("Check")}</Select.Option>
              </Select>
            </Form.Item>
            {/* TODO: use document upload component */}
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              disabled={isPageLoading || isLoading}
              block
            >
              {t("Submit")}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Content>
  );
};
